import React, { useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { shallow } from "zustand/shallow";
import {
  startTask,
  getTaskStatus,
  downloadTaskResult,
} from "../../../../api/TasksApi";
import { useAppStore } from "../../../../store/appStore";
import { useUserState } from "../../../../context/user";
import { parseSafetyFormRoute } from "../../../../services/SafetyFormRouteUtils";
import { useFilters } from "../../../../hooks/useFilters";
import { useFilterMenu } from "../../../../hooks/useFilterMenu";
import { NearMissReportingTable } from "./NearMissReportingTable";
import { SafetyAuditReportingTable } from "./SafetyAuditReportingTable";
import {
  ViewSafetyFormButton,
  RefreshSafetyFormListsButton,
  DownloadSafetyFormPdfButton,
} from "../../../landingPage/components/buttons/SafetyFormActions";
import { FilterButton } from "../../../landingPage/components/buttons/DelActions";
import { SafetyFormFilterMenu } from "../../../common/SafetyFormFilterMenu";
import { NearMissForm } from "../../../nearMissForm/NearMissForm";
import { SafetyAuditForm } from "../../../safetyAuditForm/SafetyAuditForm";
import styles from "./SafetyFormReportingTab.module.css";

export const SafetyFormReportingTab = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const user = useUserState().currentUser;

  const showLoading = useAppStore((s) => s.loadingActions.showLoading);
  const hideLoading = useAppStore((s) => s.loadingActions.hideLoading);

  const { showNotification } = useAppStore(
    (s) => s.notificationActions,
    shallow
  );

  const {
    items,
    totalCount,
    currentPage,
    pageSize,
    activeFormType,
    selectedForm,
    loading,
    selectedNearMissIds,
    selectedSafetyAuditIds,
  } = useAppStore((s) => s.safetyFormLists.safetyFormReports, shallow);

  const {
    setReportsPage,
    setReportsPageSize,
    setSelectedForm,
    setActiveFormType,
    fetchSafetyFormReportsPage,
    toggleSelectedNearMiss,
    clearSelectedNearMissIds,
    toggleSelectedSafetyAudit,
    clearSelectedSafetyAuditIds,
  } = useAppStore((s) => s.safetyFormListsActions, shallow);

  // Loading UI state
  const { isActive } = useAppStore((s) => s.loading, shallow);

  // Employees and projects for filter dropdowns
  const employees = useAppStore((s) => s.employees.list, shallow);
  const allProjectInfo = useAppStore((s) => s.projects.activeProjects, shallow);
  const allInactiveProjects = useAppStore((s) => s.projects.inactiveProjects, shallow);

  // Local filter state (resets on unmount)
  const { filters: reportsFilters, updateFilter: updateReportingFilter, clearFilters: clearReportingFilters } = useFilters();
  // Filter menu UI state
  const { showFilterMenu, toggleFilterMenu: handleToggleFilterMenu, filterMenuRef } = useFilterMenu();

  // Form state for viewing forms
  const [showForm, setShowForm] = React.useState(false);
  const [formMode, setFormMode] = React.useState(null); // 'near_miss' or 'safety_audit'

  // Near Miss Form State/Actions
  const activeNearMiss = useAppStore((s) => s.activeNearMiss, shallow);
  const activeNearMissFormActions = useAppStore(
    (s) => s.activeNearMissActions.nearMissFormActions,
    shallow
  );
  const fetchNearMiss = useAppStore(
    (s) => s.activeNearMissActions.fetchNearMiss
  );

  // Safety Audit Form State/Actions
  const activeSafetyAudit = useAppStore((s) => s.activeSafetyAuditForm, shallow);
  const activeSafetyAuditFormActions = useAppStore(
    (s) => s.activeSafetyAuditFormActions.safetyAuditFormActions,
    shallow
  );
  const fetchSafetyAudit = useAppStore(
    (s) => s.activeSafetyAuditFormActions.fetchSafetyAudit
  );

  useEffect(() => {
    if (user?.employee_id) {
      fetchSafetyFormReportsPage(user.employee_id);
    }
  }, [user?.employee_id, fetchSafetyFormReportsPage]);

  // Deep-link handler for viewing forms
  const processedRef = useRef(null);
  useEffect(() => {
    const run = async () => {
      const parsed = parseSafetyFormRoute(location.pathname, location.search);

      if (parsed.tab !== "safety_form_reporting") {
        return;
      }

      const nearMissId = parsed.nearMissId;
      const safetyAuditId = parsed.safetyAuditId;
      const formType = parsed.formType;
      const wantsOpen = !!parsed.open;

      const key = `safety_form_reporting:${formType || ""}:${
        nearMissId || safetyAuditId || ""
      }:${wantsOpen ? "1" : "0"}`;

      if (processedRef.current === key) {
        return;
      }
      processedRef.current = key;

      try {
        // If URL points to list view (no form IDs), ensure form is closed
        if (!nearMissId && !safetyAuditId) {
          setShowForm(false);
          setFormMode(null);
          return;
        }

        // Sync activeFormType with formType from URL
        if (formType && formType !== activeFormType) {
          setActiveFormType(formType);
        }

        // Handle Near Miss deep link
        if (nearMissId && formType === "near_miss" && wantsOpen) {
          // Guard: If already viewing this near miss, skip. Require a real
          // model instance (not a rehydrated plain object) so we re-fetch and
          // rebuild when prototypes were lost across a reload.
          if (
            activeNearMiss?.currentNearMiss?.id &&
            String(activeNearMiss.currentNearMiss.id) === String(nearMissId) &&
            typeof activeNearMiss.currentNearMiss.toPatch === "function"
          ) {
            setFormMode("near_miss");
            setShowForm(true);
            return;
          }

          showLoading("Loading Near Miss...");
          const res = await fetchNearMiss("view", nearMissId);
          if (res && res.ok) {
            setFormMode("near_miss");
            setShowForm(true);
          } else {
            setShowForm(false);
            setFormMode(null);
            showNotification({
              type: "error",
              title: "Error Loading Near Miss",
              message: "Near Miss not found or could not be loaded.",
            });
          }
        }
        // Handle Safety Audit deep link
        else if (
          safetyAuditId &&
          formType === "safety_audit" &&
          wantsOpen
        ) {
          // Guard: If already viewing this safety audit, skip. Require a real
          // model instance (not a rehydrated plain object) so we re-fetch and
          // rebuild when prototypes were lost across a reload.
          if (
            activeSafetyAudit?.currentSafetyAudit?.id &&
            String(activeSafetyAudit.currentSafetyAudit.id) ===
              String(safetyAuditId) &&
            typeof activeSafetyAudit.currentSafetyAudit.hasAssociatedJsa ===
              "function"
          ) {
            setFormMode("safety_audit");
            setShowForm(true);
            return;
          }

          showLoading("Loading Safety Audit...");
          const res = await fetchSafetyAudit("view", safetyAuditId);
          if (res && res.ok) {
            setFormMode("safety_audit");
            setShowForm(true);
          } else {
            setShowForm(false);
            setFormMode(null);
            showNotification({
              type: "error",
              title: "Error Loading Safety Audit",
              message: "Safety Audit not found or could not be loaded.",
            });
          }
        }
      } catch (err) {
        console.error("Failed to process Safety Form Reporting deep link", err);
      } finally {
        hideLoading();
      }
    };
    run();
  }, [
    location.pathname,
    location.search,
    activeFormType,
    setActiveFormType,
    fetchNearMiss,
    fetchSafetyAudit,
    showLoading,
    hideLoading,
    showNotification,
    activeNearMiss,
    activeSafetyAudit,
  ]);

  const handleTabSwitch = useCallback(
    async (formType) => {
      if (formType === activeFormType) return;
      showLoading("Switching tabs...");
      setActiveFormType(formType);
      await fetchSafetyFormReportsPage(user.employee_id);
      hideLoading();
    },
    [activeFormType, setActiveFormType, fetchSafetyFormReportsPage, user?.employee_id]
  );

  const handleViewForm = useCallback(
    (form) => {
      const idKey =
        activeFormType === "near_miss" ? "near_miss_id" : "safety_audit_id";
      navigate(
        `/home/safety_form_reporting/${idKey}=${form.id}?action=view&open=1&formType=${activeFormType}`
      );
    },
    [activeFormType, navigate]
  );

  const handleDoubleClick = useCallback(
    (form) => {
      handleViewForm(form);
    },
    [handleViewForm]
  );

  const handleRowSelect = useCallback(
    (form) => {
      setSelectedForm(form);
    },
    [setSelectedForm]
  );

  const handleRefresh = useCallback(async () => {
    showLoading("Refreshing data...");
    await fetchSafetyFormReportsPage(user.employee_id, reportsFilters);
    hideLoading();
  }, [fetchSafetyFormReportsPage, user?.employee_id, reportsFilters]);

  // Filter handlers
  const handleApplyFilters = useCallback(async () => {
    showLoading("Filtering...");
    setReportsPage({ currentPage: 1, pageSize });
    await fetchSafetyFormReportsPage(user.employee_id, reportsFilters);
    hideLoading();
  }, [fetchSafetyFormReportsPage, user?.employee_id, reportsFilters, setReportsPage, pageSize, showLoading, hideLoading]);

  const handleClearFilters = useCallback(async () => {
    clearReportingFilters();
    showLoading("Clearing filters...");
    setReportsPage({ currentPage: 1, pageSize });
    await fetchSafetyFormReportsPage(user.employee_id, null);
    hideLoading();
  }, [clearReportingFilters, fetchSafetyFormReportsPage, user?.employee_id, setReportsPage, pageSize, showLoading, hideLoading]);

  const handlePageChange = useCallback(
    async (direction) => {
      const newPage =
        direction === "next" ? currentPage + 1 : currentPage - 1;
      if (newPage < 1) return;
      showLoading("Loading page...");
      setReportsPage({ currentPage: newPage, pageSize });
      await fetchSafetyFormReportsPage(user.employee_id, reportsFilters);
      hideLoading();
    },
    [currentPage, pageSize, setReportsPage, fetchSafetyFormReportsPage, user?.employee_id, reportsFilters]
  );

  const handlePageSizeChange = useCallback(
    async (e) => {
      const newPageSize = parseInt(e.target.value);
      showLoading("Updating page size...");
      setReportsPageSize(newPageSize);
      await fetchSafetyFormReportsPage(user.employee_id, reportsFilters);
      hideLoading();
    },
    [setReportsPageSize, fetchSafetyFormReportsPage, user?.employee_id, reportsFilters]
  );

  // PDF Download Handlers
  const handleDownloadNearMissPdfs = useCallback(async () => {
    if (selectedNearMissIds.length === 0) {
      showNotification({
        type: "error",
        title: "No forms selected",
        message: "Please select at least one Near Miss form to download.",
      });
      return;
    }
    if (selectedNearMissIds.length > 50) {
      showNotification({
        type: "error",
        title: "Too many selected",
        message: "Please select no more than 50 forms at a time.",
      });
      return;
    }

    showLoading("Generating PDF(s)...");
    try {
      const isSingle = selectedNearMissIds.length === 1;
      const taskName = isSingle
        ? "safety_forms.create_near_miss_pdf"
        : "safety_forms.create_near_miss_pdfs";
      const params = isSingle
        ? { near_miss_id: selectedNearMissIds[0] }
        : { near_miss_ids: selectedNearMissIds };

      const { data: start } = await startTask(taskName, params);
      const taskId = start?.task_id;
      if (!taskId) throw new Error("Failed to start download task");

      // Poll for completion
      let attempts = 0;
      let ready = false;
      let lastStatus = null;
      while (!ready && attempts < 150) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: status } = await getTaskStatus(taskId);
        lastStatus = status;
        ready = !!status?.ready;
        if (ready && !status?.ok) {
          throw new Error(status?.error || "PDF generation failed");
        }
        attempts++;
      }

      if (!ready) {
        throw new Error("PDF generation timed out");
      }

      // Download the result
      const res = await downloadTaskResult(taskId, {
        s3DownloadUrl: lastStatus?.s3_download_url,
        filename: lastStatus?.filename,
      });
      if (res) {
        const blob = new Blob([res.data], { type: res.headers["content-type"] });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const cd = res.headers["content-disposition"];
        const match = cd?.match(/filename="?([^"]+)"?/);
        a.download = match ? match[1] : "NearMiss.pdf";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }

      clearSelectedNearMissIds();
      showNotification({
        type: "success",
        title: "Download Complete",
        message: `Successfully downloaded ${selectedNearMissIds.length} Near Miss PDF(s).`,
      });
    } catch (e) {
      showNotification({
        type: "error",
        title: "Download Failed",
        message: e.message || "Failed to download PDFs",
      });
    } finally {
      hideLoading();
    }
  }, [
    selectedNearMissIds,
    showLoading,
    hideLoading,
    showNotification,
    clearSelectedNearMissIds,
  ]);

  const handleDownloadSafetyAuditPdfs = useCallback(async () => {
    if (selectedSafetyAuditIds.length === 0) {
      showNotification({
        type: "error",
        title: "No forms selected",
        message: "Please select at least one Safety Audit form to download.",
      });
      return;
    }
    if (selectedSafetyAuditIds.length > 50) {
      showNotification({
        type: "error",
        title: "Too many selected",
        message: "Please select no more than 50 forms at a time.",
      });
      return;
    }

    showLoading("Generating PDF(s)...");
    try {
      const isSingle = selectedSafetyAuditIds.length === 1;
      const taskName = isSingle
        ? "safety_forms.create_safety_audit_pdf"
        : "safety_forms.create_safety_audit_pdfs";
      const params = isSingle
        ? { safety_audit_id: selectedSafetyAuditIds[0] }
        : { safety_audit_ids: selectedSafetyAuditIds };

      const { data: start } = await startTask(taskName, params);
      const taskId = start?.task_id;
      if (!taskId) throw new Error("Failed to start download task");

      // Poll for completion
      let attempts = 0;
      let ready = false;
      let lastStatus = null;
      while (!ready && attempts < 150) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: status } = await getTaskStatus(taskId);
        lastStatus = status;
        ready = !!status?.ready;
        if (ready && !status?.ok) {
          throw new Error(status?.error || "PDF generation failed");
        }
        attempts++;
      }

      if (!ready) {
        throw new Error("PDF generation timed out");
      }

      // Download the result
      const res = await downloadTaskResult(taskId, {
        s3DownloadUrl: lastStatus?.s3_download_url,
        filename: lastStatus?.filename,
      });
      if (res) {
        const blob = new Blob([res.data], { type: res.headers["content-type"] });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const cd = res.headers["content-disposition"];
        const match = cd?.match(/filename="?([^"]+)"?/);
        a.download = match ? match[1] : "SafetyAudit.pdf";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }

      clearSelectedSafetyAuditIds();
      showNotification({
        type: "success",
        title: "Download Complete",
        message: `Successfully downloaded ${selectedSafetyAuditIds.length} Safety Audit PDF(s).`,
      });
    } catch (e) {
      showNotification({
        type: "error",
        title: "Download Failed",
        message: e.message || "Failed to download PDFs",
      });
    } finally {
      hideLoading();
    }
  }, [
    selectedSafetyAuditIds,
    showLoading,
    hideLoading,
    showNotification,
    clearSelectedSafetyAuditIds,
  ]);

  // Single PDF download handler (from context menu)
  const handleDownloadSingleNearMiss = useCallback(
    async (formId) => {
      showLoading("Generating PDF...");
      try {
        const { data: start } = await startTask(
          "safety_forms.create_near_miss_pdf",
          { near_miss_id: formId }
        );
        const taskId = start?.task_id;
        if (!taskId) throw new Error("Failed to start download task");

        let attempts = 0;
        let ready = false;
        let lastStatus = null;
        while (!ready && attempts < 60) {
          await new Promise((r) => setTimeout(r, 2000));
          const { data: status } = await getTaskStatus(taskId);
          lastStatus = status;
          ready = !!status?.ready;
          if (ready && !status?.ok) {
            throw new Error(status?.error || "PDF generation failed");
          }
          attempts++;
        }

        if (!ready) throw new Error("PDF generation timed out");

        const res = await downloadTaskResult(taskId, {
          s3DownloadUrl: lastStatus?.s3_download_url,
          filename: lastStatus?.filename,
        });
        if (res) {
          const blob = new Blob([res.data], { type: res.headers["content-type"] });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          const cd = res.headers["content-disposition"];
          const match = cd?.match(/filename="?([^"]+)"?/);
          a.download = match ? match[1] : "NearMiss.pdf";
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          a.remove();
        }
      } catch (e) {
        showNotification({
          type: "error",
          title: "Download Failed",
          message: e.message || "Failed to download PDF",
        });
      } finally {
        hideLoading();
      }
    },
    [showLoading, hideLoading, showNotification]
  );

  const handleDownloadSingleSafetyAudit = useCallback(
    async (formId) => {
      showLoading("Generating PDF...");
      try {
        const { data: start } = await startTask(
          "safety_forms.create_safety_audit_pdf",
          { safety_audit_id: formId }
        );
        const taskId = start?.task_id;
        if (!taskId) throw new Error("Failed to start download task");

        let attempts = 0;
        let ready = false;
        let lastStatus = null;
        while (!ready && attempts < 60) {
          await new Promise((r) => setTimeout(r, 2000));
          const { data: status } = await getTaskStatus(taskId);
          lastStatus = status;
          ready = !!status?.ready;
          if (ready && !status?.ok) {
            throw new Error(status?.error || "PDF generation failed");
          }
          attempts++;
        }

        if (!ready) throw new Error("PDF generation timed out");

        const res = await downloadTaskResult(taskId, {
          s3DownloadUrl: lastStatus?.s3_download_url,
          filename: lastStatus?.filename,
        });
        if (res) {
          const blob = new Blob([res.data], { type: res.headers["content-type"] });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          const cd = res.headers["content-disposition"];
          const match = cd?.match(/filename="?([^"]+)"?/);
          a.download = match ? match[1] : "SafetyAudit.pdf";
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          a.remove();
        }
      } catch (e) {
        showNotification({
          type: "error",
          title: "Download Failed",
          message: e.message || "Failed to download PDF",
        });
      } finally {
        hideLoading();
      }
    },
    [showLoading, hideLoading, showNotification]
  );

  return (
    <>
      {showForm ? (
        <>
          {formMode === "near_miss" && (
            <NearMissForm
              currentNearMiss={activeNearMiss.currentNearMiss}
              nearMissMode={activeNearMiss.nearMissMode}
              nearMissFormUI={activeNearMiss.nearMissForm?.nearMissFormUI}
              photosUI={activeNearMiss.nearMissForm?.photosUI}
              rev={activeNearMiss.rev}
              nearMissFormActions={activeNearMissFormActions}
              setShowNearMissForm={setShowForm}
              autosaveErrors={activeNearMiss.nearMissForm?.autosaveErrors}
              autosaveErrorsActions={activeNearMissFormActions?.autosaveErrors}
              returnPath="/home/safety_form_reporting"
            />
          )}
          {formMode === "safety_audit" && (
            <SafetyAuditForm
              currentSafetyAudit={activeSafetyAudit.currentSafetyAudit}
              safetyAuditMode={activeSafetyAudit.safetyAuditMode}
              safetyAuditFormUI={activeSafetyAudit.safetyAuditForm?.safetyAuditFormUI}
              jsaFormUI={activeSafetyAudit.safetyAuditForm?.jsaFormUI}
              photosUI={activeSafetyAudit.safetyAuditForm?.photosUI}
              rev={activeSafetyAudit.rev}
              safetyAuditFormActions={activeSafetyAuditFormActions}
              setShowSafetyAuditForm={setShowForm}
              autosaveErrors={activeSafetyAudit.safetyAuditForm?.autosaveErrors}
              autosaveErrorsActions={activeSafetyAuditFormActions?.autosaveErrors}
              returnPath="/home/safety_form_reporting"
            />
          )}
        </>
      ) : (
        <>
          <div className={styles.reportingTabContainer}>
            <div className={styles.tabSwitcher}>
              <button
                className={
                  activeFormType === "near_miss"
                    ? `${styles.tabButton} ${styles.activeTab}`
                    : styles.tabButton
                }
                onClick={() => handleTabSwitch("near_miss")}
              >
                Near Miss Forms
              </button>
              <button
                className={
                  activeFormType === "safety_audit"
                    ? `${styles.tabButton} ${styles.activeTab}`
                    : styles.tabButton
                }
                onClick={() => handleTabSwitch("safety_audit")}
              >
                Safety Audit Forms
              </button>
            </div>

            <div className={styles.tableActions}>
              <ViewSafetyFormButton
                formName={activeFormType === "near_miss" ? "Near Miss" : "Safety Audit"}
                viewFunction={() => selectedForm && handleViewForm(selectedForm)}
                disabled={!selectedForm}
              />
            </div>

            <div className={styles.headerWithRefresh}>
              <h2>
                {activeFormType === "near_miss"
                  ? "Near Miss Reports"
                  : "Safety Audit Reports"}
              </h2>
              <div
                className={styles.headerButtons}
                style={{ position: "relative" }}
                ref={filterMenuRef}
              >
                {activeFormType === "near_miss" && (
                  <DownloadSafetyFormPdfButton
                    formName="Near Miss"
                    onClick={handleDownloadNearMissPdfs}
                    disabled={selectedNearMissIds.length === 0 || isActive}
                  />
                )}
                {activeFormType === "safety_audit" && (
                  <DownloadSafetyFormPdfButton
                    formName="Safety Audit"
                    onClick={handleDownloadSafetyAuditPdfs}
                    disabled={selectedSafetyAuditIds.length === 0 || isActive}
                  />
                )}
                <FilterButton filterFunction={handleToggleFilterMenu} />
                <RefreshSafetyFormListsButton
                  refreshFunction={handleRefresh}
                  isLoading={loading}
                />
                {showFilterMenu && (
                  <SafetyFormFilterMenu
                    formType={activeFormType}
                    filters={reportsFilters}
                    updateFilterAction={updateReportingFilter}
                    onApplyFilters={handleApplyFilters}
                    onClearFilters={handleClearFilters}
                    allEmployees={employees}
                    allProjectInfo={allProjectInfo}
                    allInactiveProjects={allInactiveProjects}
                  />
                )}
              </div>
            </div>

            <div className={styles.tableWrapper}>
              {items?.length > 0 ? (
                activeFormType === "near_miss" ? (
                  <NearMissReportingTable
                    forms={items}
                    onRowSelect={handleRowSelect}
                    onDoubleClick={handleDoubleClick}
                    selectedFormId={selectedForm?.id}
                    selectedIds={selectedNearMissIds}
                    onToggleSelect={toggleSelectedNearMiss}
                    onDownloadSingle={handleDownloadSingleNearMiss}
                    clearSelectedIds={clearSelectedNearMissIds}
                  />
                ) : (
                  <SafetyAuditReportingTable
                    forms={items}
                    onRowSelect={handleRowSelect}
                    onDoubleClick={handleDoubleClick}
                    selectedFormId={selectedForm?.id}
                    selectedIds={selectedSafetyAuditIds}
                    onToggleSelect={toggleSelectedSafetyAudit}
                    onDownloadSingle={handleDownloadSingleSafetyAudit}
                    clearSelectedIds={clearSelectedSafetyAuditIds}
                  />
                )
              ) : (
                <div className={styles.noData}>
                  {loading ? "Loading..." : "No forms to display"}
                </div>
              )}
            </div>

            <div className={styles.paginationControls}>
              <div className={styles.paginationLeftControls}>
                <button
                  onClick={() => handlePageChange("prev")}
                  disabled={currentPage === 1 || loading}
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange("next")}
                  disabled={currentPage * pageSize >= totalCount || loading}
                >
                  Next
                </button>
                <div className={styles.pageSizeSelector}>
                  <label htmlFor="pageSize">Rows:</label>
                  <select
                    id="pageSize"
                    className={styles.pageSizeSelect}
                    value={pageSize}
                    onChange={handlePageSizeChange}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="500">500</option>
                  </select>
                </div>
              </div>
              <span>
                Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)} -{" "}
                {Math.min(currentPage * pageSize, totalCount)} of {totalCount}{" "}
                {activeFormType === "near_miss" ? "near misses" : "safety audits"}
              </span>
            </div>
          </div>
        </>
      )}
    </>
  );
};
